import { useEffect, useState } from "react";
import { OktaAuthClient } from "../auth/okta";
import { isAthenaEnabled, athenaApiBase } from "./athena/config";

/**
 * Sage product access — who may use which Sage product.
 *
 * The Athena Go proxy owns the single source of truth for the email→products
 * allowlist (ADR 0004 in demo-sage-athena). The web app asks "what can *I*
 * access?" via `/api/access/me` and gets back the caller's own record — the
 * email is derived server-side from the verified bearer token, never sent as
 * a parameter. These grants drive UX only (route gating, switcher button);
 * the proxy re-enforces them on every data route.
 */

// Product identifiers as spelled in the Athena proxy's server.access list.
export const PRODUCT_BONDS = "bonds";
export const PRODUCT_ATHENA = "athena";

export type SageProduct = typeof PRODUCT_BONDS | typeof PRODUCT_ATHENA;

export type SageAccessStatus = "pending" | "ready" | "failed" | "disabled";

export interface SageAccessState {
  status: SageAccessStatus;
  hasBonds: boolean;
  hasAthena: boolean;
}

export interface SageAccess {
  email: string;
  products: string[];
}

export const fetchSageAccess = async (): Promise<SageAccess> => {
  const base = athenaApiBase();
  if (!base) {
    throw new Error("Sage access lookup: VITE_ATHENA_API_URL is not configured");
  }

  const accessToken = OktaAuthClient.getAccessToken();
  if (!accessToken) {
    throw new Error("Sage access lookup: no active access token");
  }

  const response = await fetch(`${base}/api/access/me`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    throw new Error(`Sage access lookup failed: ${response.status}`);
  }

  const data = (await response.json()) as Partial<SageAccess>;
  return { email: data.email ?? "", products: Array.isArray(data.products) ? data.products : [] };
};

const initialState = (): SageAccessState => ({
  status: isAthenaEnabled() ? "pending" : "disabled",
  hasBonds: false,
  hasAthena: false
});

// One lookup shared by every consumer (the route guards and the switcher
// buttons), deduped like getUserEmail in auth/okta.ts. A rejected lookup is
// dropped so a later mount can retry rather than caching a failure forever.
let accessPromise: Promise<string[]> | null = null;

const loadProducts = (): Promise<string[]> => {
  if (!accessPromise) {
    accessPromise = fetchSageAccess()
      .then(access => access.products)
      .catch(error => {
        accessPromise = null;
        throw error;
      });
  }
  return accessPromise;
};

export const useSageAccess = (): SageAccessState => {
  const [state, setState] = useState<SageAccessState>(initialState);

  useEffect(() => {
    if (state.status !== "pending") {
      return;
    }

    let ignore = false;
    loadProducts()
      .then(products => {
        if (ignore) {
          return;
        }
        setState({
          status: "ready",
          hasBonds: products.includes(PRODUCT_BONDS),
          hasAthena: products.includes(PRODUCT_ATHENA)
        });
      })
      .catch(error => {
        if (ignore) {
          return;
        }
        console.error("Failed to check Sage access:", error);
        setState({ status: "failed", hasBonds: false, hasAthena: false });
      });

    return () => {
      ignore = true;
    };
  }, [state.status]);

  return state;
};

/**
 * True when the user has an explicit athena grant without a bonds grant —
 * the only case that moves a user out of the Bonds chat. Everyone else
 * (bonds grant, not in the list at all, lookup failed) stays in Bonds:
 * Bonds' real authorisation is its Okta app assignment, and the list is
 * Athena's, so absence from it must not lock anyone out of Bonds.
 */
export const isAthenaOnly = (access: SageAccessState): boolean => access.status === "ready" && access.hasAthena && !access.hasBonds;
